import { useMemo, useState } from 'react'
import {
  Alert,
  Autocomplete,
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlineOutlined'
import { useSelector } from 'react-redux'

import { EmptyState, Loader } from '~/shared/ui'
import {
  useAddAssigneeGroupMember,
  useAssigneeGroupMembers,
  useRemoveAssigneeGroupMember,
} from './AssigneeGroupService'
import { providerName, type Provider } from '~/entities/provider'

interface ManageMembersBodyProps {
  groupId: number
}

/**
 * Body of the manage-members modal. Split out as its own component so the membership hooks always
 * run with a concrete group id (the wrapper renders this only when a group is selected).
 */
const ManageMembersBody = ({ groupId }: ManageMembersBodyProps) => {
  const providers = useSelector(state => state.providers)
  const { data, isPending: isLoading, isError } = useAssigneeGroupMembers(groupId)
  const addMember = useAddAssigneeGroupMember(groupId)
  const removeMember = useRemoveAssigneeGroupMember(groupId)
  const [selected, setSelected] = useState<Provider | null>(null)

  const memberUserIds = useMemo(
    () => new Set((data?.members ?? []).map(member => member.user_id)),
    [data],
  )

  // Providers not already in the group, sorted by name, offered as add candidates.
  const addOptions = useMemo(
    () =>
      providers
        .filter(provider => !memberUserIds.has(provider.id))
        .sort((a, b) => providerName(a).localeCompare(providerName(b))),
    [providers, memberUserIds],
  )

  const busy = addMember.isPending || removeMember.isPending

  const handleAdd = (provider: Provider | null) => {
    if (!provider) return
    addMember.mutate(provider.id)
    setSelected(null)
  }

  // Only show the error state on the initial load (nothing cached yet). A background refetch
  // failure — e.g. after add/remove invalidates this query — keeps the last-good list on screen
  // and lets the mutation surface its own error, rather than blanking a working list.
  if (isError && !data) {
    return (
      <Alert severity="error" data-testid="manage-members-error">
        Couldn&apos;t load this group&apos;s members. Please try again.
      </Alert>
    )
  }

  if (isLoading || !data) return <Loader />

  return (
    <Box data-testid="manage-members-body">
      {data.is_group === false && (
        <Alert severity="info" sx={{ mb: 2 }}>
          This is an individual&apos;s personal group. Anyone you add here will see work assigned to
          this person in their own queues — use it to set up coverage.
        </Alert>
      )}

      <Autocomplete
        options={addOptions}
        value={selected}
        getOptionLabel={providerName}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        onChange={(_, provider) => handleAdd(provider)}
        disabled={busy}
        renderInput={params => (
          <TextField {...params} label="Add a person" placeholder="Search by name" size="small" />
        )}
        sx={{ mb: 2 }}
        data-testid="add-member-autocomplete"
      />

      {data.members.length === 0 ? (
        <EmptyState title="No one is in this group yet." />
      ) : (
        <List data-testid="member-list">
          {data.members.map(member => (
            <ListItem
              key={member.user_id}
              divider
              secondaryAction={
                // The owner of a personal group can't be removed from their own group — only the
                // coverage members added on top of them.
                member.is_owner ? undefined : (
                  <IconButton
                    edge="end"
                    aria-label={`Remove ${member.name}`}
                    onClick={() => removeMember.mutate(member.user_id)}
                    disabled={busy}
                    data-testid={`remove-member-${member.user_id}`}
                  >
                    <DeleteOutlineIcon />
                  </IconButton>
                )
              }
            >
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography component="span">{member.name}</Typography>
                    {member.is_owner && (
                      <Chip label="Owner" size="small" color="primary" variant="outlined" />
                    )}
                    {!member.is_active && (
                      <Chip label="Inactive" size="small" color="default" variant="outlined" />
                    )}
                  </Box>
                }
                secondary={member.role ?? 'No role'}
              />
            </ListItem>
          ))}
        </List>
      )}
    </Box>
  )
}

interface ManageMembersModalProps {
  open: boolean
  onClose: () => void
  groupId: number | null
  groupName?: string
}

/**
 * Modal for viewing and editing the membership of an assignee group (team or a person's personal
 * group) from the CRM "Teams & Coverage" surface. Shared by the settings screen and the members
 * dashboard team-filter entry point.
 */
const ManageMembersModal = ({ open, onClose, groupId, groupName }: ManageMembersModalProps) => (
  <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
    <DialogTitle sx={{ pr: 6 }}>
      {groupName ?? 'Manage members'}
      <IconButton
        aria-label="Close"
        onClick={onClose}
        sx={{ position: 'absolute', right: 8, top: 8 }}
        data-testid="close-manage-members"
      >
        <CloseIcon />
      </IconButton>
    </DialogTitle>
    <DialogContent dividers>
      {groupId != null && <ManageMembersBody groupId={groupId} />}
    </DialogContent>
  </Dialog>
)

export default ManageMembersModal
